import type {Canvas, CanvasEvents} from '@postermywall/fabricjs-2';
import {GUIDELINES_COLOR, GUIDELINES_MARGIN} from '@PosterWhiteboard/page/page.types';
import {AlignType} from '@PosterWhiteboard/page/alignment.class';

export function initCanvasEdgeGuidelines(canvas: Canvas) {
  const isPosterEditorCanvas = canvas.lowerCanvasEl.closest('#poster-whiteboard-container') !== null;

  const getTotalNonZoomedWidth = (): number => {
    if (isPosterEditorCanvas) {
      return window.posterEditor?.whiteboard?.width ?? 0;
    }

    return canvas.getWidth() / canvas.getZoom();
  };

  const getTotalNonZoomedHeight = (): number => {
    if (isPosterEditorCanvas) {
      return window.posterEditor?.whiteboard?.height ?? 0;
    }

    return canvas.getHeight() / canvas.getZoom();
  };

  let canvasWidth = getTotalNonZoomedWidth();
  let canvasHeight = getTotalNonZoomedHeight();

  // Edge maps for snap detection
  let canvasTopMap: Record<number, true> = {};
  let canvasLeftMap: Record<number, true> = {};
  let canvasBottomMap: Record<number, true> = {};
  let canvasRightMap: Record<number, true> = {};

  const lineMargin = GUIDELINES_MARGIN;
  const lineWidth = 1;
  const ctx = canvas.getSelectionContext();

  const showEdgeLine = (x1: number, y1: number, x2: number, y2: number): void => {
    const zoom = canvas.getZoom();
    const viewportTransform = canvas.viewportTransform;

    ctx.save();
    ctx.transform(...viewportTransform);
    ctx.strokeStyle = GUIDELINES_COLOR;
    ctx.lineWidth = lineWidth / zoom;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.restore();
  };

  const showTopLine = (): void => {
    showEdgeLine(0, 0.5, canvasWidth, 0.5);
  };

  const showLeftLine = (): void => {
    showEdgeLine(0.5, 0, 0.5, canvasHeight);
  };

  const showBottomLine = (): void => {
    showEdgeLine(0, canvasHeight - 0.5, canvasWidth, canvasHeight - 0.5);
  };

  const showRightLine = (): void => {
    showEdgeLine(canvasWidth - 0.5, 0, canvasWidth - 0.5, canvasHeight);
  };

  let isTop = false;
  let isLeft = false;
  let isBottom = false;
  let isRight = false;

  function mouseDown(): void {
    let i;
    let len;

    canvasWidth = getTotalNonZoomedWidth();
    canvasHeight = getTotalNonZoomedHeight();

    canvasTopMap = {};
    for (i = 0, len = lineMargin; i <= len; i++) {
      canvasTopMap[Math.round(i)] = true;
    }

    canvasLeftMap = {};
    for (i = 0, len = lineMargin; i <= len; i++) {
      canvasLeftMap[Math.round(i)] = true;
    }

    canvasBottomMap = {};
    for (i = canvasHeight - lineMargin, len = canvasHeight; i <= len; i++) {
      canvasBottomMap[Math.round(i)] = true;
    }

    canvasRightMap = {};
    for (i = canvasWidth - lineMargin, len = canvasWidth; i <= len; i++) {
      canvasRightMap[Math.round(i)] = true;
    }
  }

  function moving(e: CanvasEvents['object:moving']): void {
    const object = e.target;
    const transform = canvas._currentTransform;

    if (!transform) return;

    const bounds = object.getBoundingRect();

    isTop = Math.round(bounds.top) in canvasTopMap;
    isLeft = Math.round(bounds.left) in canvasLeftMap;
    isBottom = Math.round(bounds.top + bounds.height) in canvasBottomMap;
    isRight = Math.round(bounds.left + bounds.width) in canvasRightMap;

    if (isTop) {
      window.posterEditor?.whiteboard?.getCurrentPage().activeSelection.alignment.alignItems(AlignType.TOP, false, false);
    }

    if (isLeft) {
      window.posterEditor?.whiteboard?.getCurrentPage().activeSelection.alignment.alignItems(AlignType.LEFT, false, false);
    }

    if (isBottom) {
      window.posterEditor?.whiteboard?.getCurrentPage().activeSelection.alignment.alignItems(AlignType.BOTTOM, false, false);
    }

    if (isRight) {
      window.posterEditor?.whiteboard?.getCurrentPage().activeSelection.alignment.alignItems(AlignType.RIGHT, false, false);
    }
  }

  function beforeRender(): void {
    if (canvas.contextTop) {
      canvas.clearContext(canvas.contextTop);
    }
  }

  function afterRender(): void {
    if (isTop) {
      showTopLine();
    }
    if (isLeft) {
      showLeftLine();
    }
    if (isBottom) {
      showBottomLine();
    }
    if (isRight) {
      showRightLine();
    }
  }

  function mouseUp(): void {
    isTop = false;
    isLeft = false;
    isBottom = false;
    isRight = false;
    canvas.renderAll();
  }

  canvas.on('object:moving', moving);
  canvas.on('before:render', beforeRender);
  canvas.on('after:render', afterRender);
  canvas.on('mouse:up', mouseUp);
  canvas.on('mouse:down', mouseDown);

  return (): void => {
    canvas.off('object:moving', moving);
    canvas.off('before:render', beforeRender);
    canvas.off('after:render', afterRender);
    canvas.off('mouse:up', mouseUp);
    canvas.off('mouse:down', mouseDown);
  };
}
